import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { EVENTO_ASSINATURA_ALTERADA } from '../lib/eventos-assinatura';
import { useAuthStore } from '../store/auth.store';
import { usePermissao } from '../hooks/use-permissao';
import { leituraDaTela } from '../lib/telas-acesso';

/**
 * Banner de countdown do trial (REQ-008 Fluxo 2 / AB#45).
 *
 * Fica logo abaixo do `ImpersonateBanner` (que ocupa o topo da página, z-50).
 * Carrega o estado do trial a partir de GET /auth/me — a resposta inclui
 * `trial_info` apenas quando o tenant está em TRIAL ou TRIAL_EXPIRADO; para
 * tenants ATIVO/SUSPENSO/CANCELADO/INADIMPLENTE o banner permanece oculto.
 *
 * Cores escalonam com a urgência:
 *   > 7 dias  → azul (informativo)
 *   ≤ 7 dias  → âmbar (atenção)
 *   ≤ 2 dias  → vermelho (urgente)
 *   = 0 dias  → vermelho com texto "expira hoje"
 *   < 0 dias  → componente NÃO renderiza (o bloqueio fica para AB#47).
 */

interface TrialInfo {
  dias_restantes: number;
  data_fim_trial: string;
  status: 'TRIAL' | 'TRIAL_EXPIRADO';
}

export function TrialBanner(): JSX.Element | null {
  const access_token = useAuthStore((s) => s.access_token);
  const usuario = useAuthStore((s) => s.usuario);
  const { pode } = usePermissao();
  const [trial, setTrial] = useState<TrialInfo | null>(null);

  const recarregar = useCallback(async () => {
    if (!usuario || !access_token) {
      setTrial(null);
      return;
    }
    try {
      const res = await apiFetch('/auth/me');
      if (!res.ok) return;
      const data = (await res.json()) as { trial_info?: TrialInfo };
      setTrial(data.trial_info ?? null);
      // [Bug#357] AUTO-CORREÇÃO da flag do auth.store, que o TenantLayout usa
      // pra barrar rotas sem flash. A flag é cache: se ficasse presa em `true`
      // depois do tenant ASSINAR, ele seria expulso de toda rota pra sempre —
      // justo quem acabou de pagar. Este componente monta no shell e o /auth/me
      // é a fonte fresca, então a divergência se corrige sozinha.
      useAuthStore.getState().setTrialExpirado(data.trial_info?.status === 'TRIAL_EXPIRADO');
    } catch {
      /* ignore */
    }
  }, [usuario, access_token]);

  useEffect(() => {
    void recarregar();
    // Refetch quando a aba volta a ficar visível — pega mudanças feitas pelo
    // Super Admin ou pela passagem do dia sem precisar de hard reload.
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') void recarregar();
    };
    document.addEventListener('visibilitychange', onVisibility);
    // [AB#437] Contratar um plano no meio do trial flipa o tenant pra ATIVO e o
    // `/auth/me` deixa de mandar `trial_info` — mas isto vive no shell, fora da
    // tela de assinatura. Sem este listener, quem acabou de pagar continuaria
    // vendo "seu trial termina em X dias" até o heartbeat de 60s virar.
    const onAssinaturaAlterada = (): void => void recarregar();
    window.addEventListener(EVENTO_ASSINATURA_ALTERADA, onAssinaturaAlterada);
    // Heartbeat de 60s — `dias_restantes` muda no máximo 1x por dia, mas o
    // intervalo curto deixa o teste e a UX imediatos quando o admin altera datas.
    const iv = window.setInterval(() => void recarregar(), 60_000);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener(EVENTO_ASSINATURA_ALTERADA, onAssinaturaAlterada);
      window.clearInterval(iv);
    };
  }, [recarregar]);

  // Sem trial OU já expirado (negativo): não renderiza (AB#47 cuida do bloqueio).
  if (!trial || trial.status !== 'TRIAL' || trial.dias_restantes < 0) {
    return null;
  }

  const { dias_restantes } = trial;
  const tom =
    dias_restantes <= 2 ? 'red' : dias_restantes <= 7 ? 'amber' : 'blue';

  const classes = {
    blue: 'border-blue-200 bg-blue-50 text-blue-900',
    amber: 'border-amber-300 bg-amber-50 text-amber-900',
    red: 'border-rose-300 bg-rose-50 text-rose-900',
  }[tom];

  const botaoClasses = {
    blue: 'bg-blue-600 hover:bg-blue-700',
    amber: 'bg-amber-600 hover:bg-amber-700',
    red: 'bg-rose-600 hover:bg-rose-700',
  }[tom];

  const mensagem =
    dias_restantes === 0
      ? 'Seu trial expira hoje!'
      : dias_restantes === 1
        ? 'Seu trial expira em 1 dia. Contrate um plano para continuar.'
        : `Seu trial expira em ${dias_restantes} dias. Contrate um plano para continuar.`;

  return (
    <div
      role="status"
      className={`sticky top-0 z-40 flex w-full items-center justify-between gap-4 border-b px-6 py-2.5 text-sm shadow-sm ${classes}`}
    >
      <p className="font-medium">{mensagem}</p>
      {/* [AB#907] `config.assinatura:ler` é LIVRE (só Administrador por padrão) — sem a
          permissão, o link viraria só um 403 (`AcessoNegado`) na frente; troca por um aviso
          inline, nunca esconde sem explicação (ADR-069 §11 item 7). */}
      {pode(leituraDaTela('mc-ass')) ? (
        <Link
          to="/minha-conta/assinatura"
          className={`whitespace-nowrap rounded-md px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-white transition ${botaoClasses}`}
        >
          Ver planos
        </Link>
      ) : (
        <span className="whitespace-nowrap text-xs font-medium text-amber-800">
          Peça ao administrador da sua empresa
        </span>
      )}
    </div>
  );
}
